import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Grid,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import OTPInput from "../../dashboard/components/Otp/index";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useAuth } from "context";
import {
  getAllRestaurantAPI,
  createCustomerAndSendOtp,
  generateCodeApi,
  getIncentiveForRestaurant,
} from "../_request";
import { verifyOtp } from "../../../layouts/authentication/_request";

const DeliveryForm = ({ code, setCode }) => {
  const { showSnackbarWithMessage } = useAuth();
  const [formData, setFormData] = useState({
    restaurant: null,
    driverDetail: "",
    orderId: "",
    deliveryDate: "",
    isMarketplaceOrder: false,
  });

  const [allRestaurants, setAllRestaurants] = useState([]);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [incentive, setIncentive] = useState(null);

  const [isFormValid, setIsFormValid] = useState(false);
  const getAllRestaurants = async (search = "") => {
    const {
      data: { success, data },
    } = await getAllRestaurantAPI(search);
    if (success) {
      setAllRestaurants(
        data.restaurantStores.map((restaurant) => ({
          id: restaurant.id,
          name: restaurant.name,
          city: restaurant.city,
          state: restaurant.state,
        }))
      );
    }
  };
  useEffect(() => {
    getAllRestaurants();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRestaurantChange = async (event, value) => {
    setFormData({ ...formData, restaurant: value });
    if (value) {
      try {
        const {
          data: { data, success },
        } = await getIncentiveForRestaurant(value.id);
        if (success) {
          setIncentive(data);
        } else {
          setIncentive(null);
        }
      } catch (error) {
        setIncentive(null);
      }
    } else {
      setIncentive(null);
    }
  };

  const validateForm = () => {
    const newErrors = {
      restaurant: !formData.restaurant,
      driverDetail: !formData.driverDetail,
      deliveryDate: !formData.deliveryDate,
    };
    // Check if any of the required fields are empty
    return !Object.values(newErrors).includes(true);
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [formData]);

  const handleGenerateCode = async () => {
    if (!isFormValid) {
      showSnackbarWithMessage({
        type: "error",
        message: "Please fill in all required fields.",
      });
      return;
    }
    try {
      const {
        data: { success, message },
      } = await createCustomerAndSendOtp({ formData });
      if (success) {
        setOtpSent(true);
        showSnackbarWithMessage({
          type: "success",
          message,
        });
      }
    } catch (e) {}
  };

  const handleVerifyOtp = async () => {
    try {
      const { data: verifyOtpResponse } = await verifyOtp({
        identifier: formData.driverDetail,
        otp: otp,
      });

      if (verifyOtpResponse.success) {
        const { accessToken } = verifyOtpResponse.data.tokens;
        const { data: generateCodeResponse } = await generateCodeApi({
          formData,
          accessToken,
        });
        if (generateCodeResponse.success) {
          setCode(generateCodeResponse.data.code);
          showSnackbarWithMessage({
            type: "success",
            message: generateCodeResponse.message,
          });
        } else {
          showSnackbarWithMessage({
            type: "error",
            message: generateCodeResponse.message || "Failed to generate referral code.",
          });
        }
      } else {
        showSnackbarWithMessage({
          type: "error",
          message: verifyOtpResponse.message || "OTP verification failed.",
        });
      }
    } catch (error) {
      console.error("Error in handleVerifyOtp:", error);
      showSnackbarWithMessage({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      });
    }
  };

  const handleResendOtp = () => {
    // Logic to resend OTP
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  const isEmailValid = (email) => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailPattern.test(email);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={allRestaurants}
          getOptionLabel={(option) => `${option.name}, ${option.city}`}
          value={formData.restaurant}
          onChange={handleRestaurantChange}
          onInputChange={(event, newInputValue) => {
            getAllRestaurants(newInputValue);
          }}
          renderInput={(params) => (
            <MDInput
              {...params}
              label="Select Restaurant"
              variant="outlined"
              fullWidth
              disabled={otpSent}
              required
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <MDInput
          label="Driver Email"
          variant="outlined"
          fullWidth
          name="driverDetail"
          value={formData.driverDetail}
          onChange={handleChange}
          disabled={otpSent}
          error={!isEmailValid(formData.driverDetail)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <MDInput
          label="Order ID"
          variant="outlined"
          fullWidth
          name="orderId"
          value={formData.orderId}
          onChange={handleChange}
          disabled={otpSent}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <MDInput
          label="Delivery Date"
          variant="outlined"
          fullWidth
          name="deliveryDate"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={formData.deliveryDate}
          onChange={handleChange}
          disabled={otpSent}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.isMarketplaceOrder}
              onChange={handleCheckboxChange}
              name="isMarketplaceOrder"
              color="primary"
              disabled={otpSent}
            />
          }
          label="Is this a marketplace order?"
        />
      </Grid>
      {incentive ? (
        <Grid item xs={12}>
          <Typography variant="h6">Incentive Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Driver</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ padding: "8px", fontWeight: "bold" }}>
                        Reward Condition
                      </TableCell>
                      <TableCell align="right" style={{ padding: "8px", fontWeight: "bold" }}>
                        Reward Type
                      </TableCell>
                      <TableCell align="right" style={{ padding: "8px", fontWeight: "bold" }}>
                        Fixed Cashback
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incentive.incentives_details?.DRIVER?.map((detail) => (
                      <TableRow key={detail.id}>
                        <TableCell style={{ padding: "8px" }}>
                          {detail.rewardCondition === "FIRST_ORDER"
                            ? "NEXT_ORDER"
                            : detail.rewardCondition}
                        </TableCell>
                        <TableCell align="right" style={{ padding: "8px" }}>
                          {detail.rewardType}
                        </TableCell>
                        <TableCell align="right" style={{ padding: "8px" }}>
                          {detail.fixedCashback}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Customer</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ padding: "8px", fontWeight: "bold" }}>
                        Reward Condition
                      </TableCell>
                      <TableCell align="right" style={{ padding: "8px", fontWeight: "bold" }}>
                        Reward Type
                      </TableCell>
                      <TableCell align="right" style={{ padding: "8px", fontWeight: "bold" }}>
                        Fixed Cashback
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incentive.incentives_details?.CUSTOMER?.map((detail) => (
                      <TableRow key={detail.id}>
                        <TableCell style={{ padding: "8px" }}>
                          {detail.rewardCondition === "FIRST_ORDER"
                            ? "NEXT_ORDER"
                            : detail.rewardCondition}
                        </TableCell>
                        <TableCell align="right" style={{ padding: "8px" }}>
                          {detail.rewardType}
                        </TableCell>
                        <TableCell align="right" style={{ padding: "8px" }}>
                          {detail.fixedCashback}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Typography variant="h6" color="error">
            No Incentive Information Available
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <Grid container justifyContent="flex-end">
          <Grid item xs={12} md={6}>
            <MDButton
              variant="gradient"
              color="success"
              fullWidth
              onClick={handleGenerateCode}
              disabled={otpSent || !isFormValid || !isEmailValid(formData.driverDetail)}
            >
              Generate Verification Code
            </MDButton>
          </Grid>
        </Grid>
      </Grid>
      {otpSent && !code && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6" align="center">
              Please enter the verification code and verify your email/phonenumber
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ marginTop: "15px", marginBottom: "5px" }}>
              <OTPInput otp={otp} setOtp={setOtp} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            {/* <MDButton variant="text" color={"dark"} onClick={handleResendOtp}>
              Resend OTP
            </MDButton> */}
          </Grid>
          <Grid item xs={12}>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12} md={6}>
                <MDButton
                  variant="gradient"
                  color={"light"}
                  fullWidth
                  onClick={handleVerifyOtp}
                  disabled={otp.length !== 6}
                >
                  Verify
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

DeliveryForm.propTypes = {
  code: PropTypes.string.isRequired,
  setCode: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    driverDetail: PropTypes.string.isRequired,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  otpSent: PropTypes.bool.isRequired,
};

export default DeliveryForm;
